// src/pages/profile.js
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import {
  Calendar,
  Clock,
  CreditCard,
  Eye, EyeOff,
  Loader2,
  Lock,
  LogOut,
  MapPin,
  MessageSquare,
  Package,
  Save,
  Settings,
  Tag,
  User
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AddressAutocomplete from '../components/AddressAutocomplete';
import BusinessUserPayment from '../components/BusinessUserPayment';
import PaymentMethodsManagement from '../components/PaymentMethodsManagement';
import SupportSection from '../components/SupportSection';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import axios from "../utils/axiosHandler";

const Profile = () => {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('personal');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', content: '' });
  const [orders, setOrders] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const { config } = useConfig();

  //for card info
  const [showBusinessUpgradeForm, setShowBusinessUpgradeForm] = useState(false);
  const [businessPaymentIntentId, setBusinessPaymentIntentId] = useState(null);
  const [businessPaymentMethodId, setBusinessPaymentMethodId] = useState(null);
  const [businessPaymentProcessing, setBusinessPaymentProcessing] = useState(false);
  const [businessPaymentError, setBusinessPaymentError] = useState(null);
  const [showAddPaymentForm, setShowAddPaymentForm] = useState(false);
  const [paymentMethodError, setPaymentMethodError] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [isStripeLoading, setIsStripeLoading] = useState(true);
  const [stripeError, setStripeError] = useState(null); // Added for error handling
  const [paymentIntentId, setPaymentIntentId] = useState(false);
  const [paymentMethodId, setPaymentMethodId] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    currentPassword: '',
    newPassword: '',
    addresses: [],
    defaultAddress: '',
    userType: 'regular',
    preferences: {
      newsletter: true,
      marketingEmails: true,
      orderUpdates: true,
      promotionalAlerts: true
    }
  });

  const [loginActivity, setLoginActivity] = useState([]);
  const [showAllActivity, setShowAllActivity] = useState(false);
  const [loadingActivity, setLoadingActivity] = useState(false);

  const [bookings, setBookings] = useState([]);
  const [hasBookings, setHasBookings] = useState(false);
  const [loadingBookings, setLoadingBookings] = useState(false);

  const [hasRoadTestRequests, setHasRoadTestRequests] = useState(false);
  // NEW: road test requests state
  const [roadTestRequests, setRoadTestRequests] = useState([]);
  const [loadingRoadTests, setLoadingRoadTests] = useState(false);

  // Fetch the Stripe public key from the backend on mount
  useEffect(() => {
    const fetchStripeKey = async () => {
      try {
        setIsStripeLoading(true);
        const { data } = await axios.get('/api/config/stripe');

        if (data.publicKey) {
          // Create the promise with the public key
          const promise = await loadStripe(data.publicKey);
          setStripePromise(promise);
        } else {
          throw new Error('No public key in response');
        }
      } catch (err) {
        console.error("Error fetching Stripe public key:", err);
        setStripeError("Could not load payment system. Please try again later.");
      } finally {
        setIsStripeLoading(false);
      }
    };

    fetchStripeKey();
  }, []);

  // Read tab from query string and select it if allowed
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab) {
      setActiveTab(tab);
    }
  }, [location.search]);

  // Prevent selecting road-test tab if user has none
  useEffect(() => {
    if (activeTab === 'road-test' && !hasRoadTestRequests) {
      setActiveTab('personal');
    }
  }, [activeTab, hasRoadTestRequests]);

  useEffect(() => {
    const fetchLoginActivity = async () => {
      if (activeTab === 'security') {
        try {
          setLoadingActivity(true);
          const response = await axios.get('/api/users/login-activity', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          const data = await response.data;
          setLoginActivity(data);
        } catch (error) {
          console.error('Error fetching login activity:', error);
        } finally {
          setLoadingActivity(false);
        }
      }
    };

    fetchLoginActivity();
  }, [activeTab]);

  useEffect(() => {
    if (user) {
      setFormData(prevState => ({
        ...prevState,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || '',
        company: user.company || '',
        userType: user.userType || 'regular',
        addresses: user.addresses || [],
        defaultAddress: user.defaultAddress || '',
        preferences: {
          newsletter: user.preferences?.newsletter ?? true,
          marketingEmails: user.preferences?.marketingEmails ?? true,
          orderUpdates: user.preferences?.orderUpdates ?? true,
          promotionalAlerts: user.preferences?.promotionalAlerts ?? true
        },
        paymentMethods: user.paymentMethods || [],
      }));
    }

    // Fetch orders if on orders tab
    if (activeTab === 'orders') {
      fetchOrders();
    }

    // Fetch coupons if on coupons tab
    if (activeTab === 'coupons') {
      fetchCoupons();
    }

    // Fetch bookings if on reservations tab
    if (activeTab === 'reservations') {
      fetchBookings();
    }
  }, [user, activeTab]);

  useEffect(() => {
    const checkUserBookings = async () => {
      try {
        const response = await axios.get('/api/users/has-bookings', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        setHasBookings(response.data.hasBookings);
      } catch (error) {
        console.error('Error checking user bookings:', error);
      }
    };

    checkUserBookings();
  }, []);

  // Check if user has road test requests (current tenant)
  useEffect(() => {
    const checkRoadTests = async () => {
      try {
        const response = await axios.get('/api/driving-schools/requests?mine=true&limit=1', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const hasAny = Array.isArray(response.data.data) && response.data.data.length > 0;
        setHasRoadTestRequests(hasAny);
      } catch (e) {
        console.error('Error checking road test requests:', e);
        setHasRoadTestRequests(false);
      }
    };
    checkRoadTests();
  }, []);

  // NEW: fetch road test requests when the tab is active
  useEffect(() => {
    if (activeTab === 'road-test') {
      fetchRoadTests();
    }
  }, [activeTab]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get('/api/users/orders', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.data;
      setOrders(data);
    } catch (error) {
      setMessage({ type: 'error', content: 'Failed to fetch orders' });
    }
  };

  const fetchCoupons = async () => {
    try {
      const response = await axios.get('/api/users/coupons', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.data;
      setCoupons(data);
    } catch (error) {
      setMessage({ type: 'error', content: 'Failed to fetch coupons' });
    }
  };

  const fetchBookings = async () => {
    try {
      setLoadingBookings(true);
      const response = await axios.get('/api/users/bookings', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setBookings(response.data.bookings);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setMessage({ type: 'error', content: 'Failed to fetch bookings' });
    } finally {
      setLoadingBookings(false);
    }
  };

  // NEW: fetch road tests helper
  const fetchRoadTests = async () => {
    try {
      setLoadingRoadTests(true);
      const response = await axios.get('/api/driving-schools/requests?mine=true&limit=50', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setRoadTestRequests(Array.isArray(response.data.data) ? response.data.data : []);
    } catch (error) {
      console.error('Error fetching road test requests:', error);
    } finally {
      setLoadingRoadTests(false);
    }
  };

  const CopyButton = ({ code }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = (e) => {
      e.preventDefault();
      navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };

    return (
      <button
        onClick={handleCopy}
        className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors"
      >
        {copied ? 'Copied!' : 'Copy Code'}
      </button>
    );
  };

  const handleAddAddress = () => {
    setFormData(prev => ({
      ...prev,
      addresses: [...prev.addresses, { street: '', city: '', state: '', postalCode: '', country: 'Canada' }]
    }));
  };

  const handleRemoveAddress = (index) => {
    setFormData(prev => ({
      ...prev,
      addresses: prev.addresses.filter((_, i) => i !== index)
    }));
  };

  const handleAddressChange = (index, field, value) => {
    setFormData(prev => {
      const updatedAddresses = [...prev.addresses];
      updatedAddresses[index] = { ...updatedAddresses[index], [field]: value };
      return { ...prev, addresses: updatedAddresses };
    });
  };

  const handleSetDefaultAddress = (index) => {
    setFormData(prev => ({
      ...prev,
      defaultAddress: prev.addresses[index]._id
    }));
  };

  const handleAccountTypeChange = async (newType) => {
    // If no change, do nothing
    if (newType === formData.userType) {
      return;
    }

    // If changing to regular, no payment needed
    if (newType === 'regular') {
      try {
        setLoading(true);
        const response = await axios.put('/api/users/update-account-type', {
          userType: 'regular'
        }, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        const data = response.data;

        if (response.status === 200) {
          // Update local state first
          setFormData(prev => ({ ...prev, userType: 'regular' }));

          // Update localStorage
          const userJson = localStorage.getItem('user');
          if (userJson) {
            const userObj = JSON.parse(userJson);
            userObj.userType = 'regular';
            localStorage.setItem('user', JSON.stringify(userObj));
          }

          // Store new token if provided
          if (data.token) {
            localStorage.setItem('token', data.token);
            axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
          }

          setMessage({ type: 'success', content: 'Account type updated to Regular' });

          // Clear success message after 3 seconds
          setTimeout(() => {
            setMessage({ type: '', content: '' });
          }, 3000);
        } else {
          setMessage({ type: 'error', content: data.error || 'Failed to update account type' });
        }
      } catch (error) {
        console.error('Account type update error:', error);
        setMessage({ type: 'error', content: 'An error occurred while updating account type' });
      } finally {
        setLoading(false);
      }
    } else if (newType === 'business') {
      // Show business upgrade form for verification payment
      setShowBusinessUpgradeForm(true);
    }
  };

  // function to handle business verification payment completion
  const handleBusinessVerificationComplete = async (paymentIntentId, paymentMethodId) => {
    setBusinessPaymentIntentId(paymentIntentId);
    setBusinessPaymentMethodId(paymentMethodId);
    setBusinessPaymentProcessing(true);

    try {
      const response = await axios.put('/api/users/update-account-type', {
        userType: 'business',
        paymentIntentId,
        paymentMethodId: paymentMethodId || undefined
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.data;

      if (response.status === 200) {
        // Update local state
        setFormData(prev => ({
          ...prev,
          userType: 'business',
          businessVerified: true
        }));

        // Update localStorage
        const userJson = localStorage.getItem('user');
        if (userJson) {
          const userObj = JSON.parse(userJson);
          userObj.userType = 'business';
          userObj.businessVerified = true;
          localStorage.setItem('user', JSON.stringify(userObj));
        }

        // Store new token if provided
        if (data.token) {
          localStorage.setItem('token', data.token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
        }

        setMessage({ type: 'success', content: 'Account upgraded to Business successfully!' });
        setShowBusinessUpgradeForm(false);

        // Clear success message after 3 seconds
        setTimeout(() => {
          setMessage({ type: '', content: '' });
        }, 3000);
      } else {
        setMessage({ type: 'error', content: data.error || 'Failed to upgrade account' });
      }
    } catch (error) {
      console.error('Business verification error:', error);
      setMessage({ type: 'error', content: 'An error occurred during business verification' });
    } finally {
      setBusinessPaymentProcessing(false);
    }
  };

  const handleAddPaymentMethod = (updatedPaymentMethods) => {
    setFormData(prev => ({
      ...prev,
      paymentMethods: updatedPaymentMethods
    }));
    setShowAddPaymentForm(false);
    setMessage({ type: 'success', content: 'Payment method added successfully' });

    // Update user object in localStorage
    const userJson = localStorage.getItem('user');
    if (userJson) {
      const user = JSON.parse(userJson);
      user.paymentMethods = updatedPaymentMethods;
      localStorage.setItem('user', JSON.stringify(user));
    }
  };

  const handleSetDefaultPaymentMethod = async (paymentMethodId) => {
    try {
      const response = await axios.post('/api/users/payment-methods', {
        paymentMethodId,
        makeDefault: true
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.data;

      if (response.status === 200) {
        setFormData(prev => ({
          ...prev,
          paymentMethods: data.paymentMethods
        }));
        setMessage({ type: 'success', content: 'Default payment method updated' });

        // Update user object in localStorage
        const userJson = localStorage.getItem('user');
        if (userJson) {
          const user = JSON.parse(userJson);
          user.paymentMethods = data.paymentMethods;
          localStorage.setItem('user', JSON.stringify(user));
        }
      } else {
        setMessage({ type: 'error', content: data.error || 'Failed to update payment method' });
      }
    } catch (error) {
      setMessage({ type: 'error', content: 'An error occurred while updating payment method' });
    }
  };

  const handleDeletePaymentMethod = async (paymentMethodId) => {
    if (window.confirm('Are you sure you want to remove this payment method?')) {
      try {
        const response = await axios.delete(`/api/users/payment-methods/${paymentMethodId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
        });

        const data = await response.data;

        if (response.status === 200) {
          setFormData(prev => ({
            ...prev,
            paymentMethods: data.paymentMethods
          }));
          setMessage({ type: 'success', content: 'Payment method removed' });

          // Update user object in localStorage
          const userJson = localStorage.getItem('user');
          if (userJson) {
            const user = JSON.parse(userJson);
            user.paymentMethods = data.paymentMethods;
            localStorage.setItem('user', JSON.stringify(user));
          }
        } else {
          setMessage({ type: 'error', content: data.error || 'Failed to remove payment method' });
        }
      } catch (error) {
        setMessage({ type: 'error', content: 'An error occurred while removing payment method' });
      }
    }
  };

  const handleDeleteAccount = async () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      try {
        const response = await axios.delete('/api/users/profile', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
        });

        if (response.status === 200) {
          logout();
          navigate('/');
        } else {
          setMessage({ type: 'error', content: 'Failed to delete account' });
        }
      } catch (error) {
        setMessage({ type: 'error', content: 'An error occurred while deleting account' });
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.includes('.')) {
      const [section, field] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', content: '' });

    try {
      const response = await axios.put('/api/users/profile', formData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
      });

      const data = await response.data;

      if (response.status === 200) {
        // Update localStorage with new user data
        localStorage.setItem('user', JSON.stringify(data.user));

        // Update token if provided
        if (data.token) {
          localStorage.setItem('token', data.token);
          // Update axios default headers for future requests
          axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
        }

        // Update local state with new user data
        setFormData(prev => ({
          ...prev,
          ...data.user
        }));

        // CRITICAL FIX: Update the global user state in AuthContext
        await refreshUser();

        setMessage({ type: 'success', content: 'Profile updated successfully!' });

        // Clear success message after 3 seconds
        setTimeout(() => {
          setMessage({ type: '', content: '' });
        }, 3000);

      } else {
        setMessage({ type: 'error', content: data.error || 'Failed to update profile' });
      }
    } catch (error) {
      console.error('Profile update error:', error);

      // Provide more specific error messages
      if (error.response?.data?.error) {
        setMessage({ type: 'error', content: error.response.data.error });
      } else if (error.response?.status === 500) {
        setMessage({ type: 'error', content: 'Server error occurred. Please try again.' });
      } else {
        setMessage({ type: 'error', content: 'An error occurred while updating profile' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Build tabs with conditional road-test/reservations
  const baseTabs = useMemo(() => ([
    { id: 'personal', label: 'Account Info', icon: User },
    { id: 'address', label: 'Address', icon: MapPin },
    { id: 'orders', label: 'Orders', icon: Package },
    { id: 'payments', label: 'Payment Methods', icon: CreditCard },
    { id: 'coupons', label: 'My Coupons', icon: Tag },
    { id: 'notifications', label: 'Notifications', icon: MessageSquare },
    { id: 'support', label: 'Support', icon: MessageSquare },
    { id: 'security', label: 'Security', icon: Lock },
    { id: 'preferences', label: 'Preferences', icon: Settings }
  ]), []);

  const tabs = useMemo(() => {
    const t = [...baseTabs];
    if (hasBookings) {
      t.splice(3, 0, { id: 'reservations', label: 'Reservations', icon: Calendar });
    }
    if (hasRoadTestRequests) {
      t.splice(5, 0, { id: 'road-test', label: 'Road Test Booking', icon: Calendar });
    }
    return t;
  }, [baseTabs, hasBookings, hasRoadTestRequests]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'personal':
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold mb-4">Personal Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Company (Optional)</label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Account Type</label>
                <div className="space-y-2 mt-2">
                  <div className="flex items-center justify-between p-3 border rounded-md">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="regular-account"
                        name="userType"
                        checked={formData.userType === 'regular'}
                        onChange={(e) => {
                          e.preventDefault(); // Prevent form submission
                          if (formData.userType !== 'regular') {
                            if (window.confirm('Are you sure you want to change your account to Regular?')) {
                              handleAccountTypeChange('regular');
                            }
                          }
                        }}
                        className="mr-2"
                      />
                      <label htmlFor="regular-account">
                        <span className="font-medium">Regular Customer</span>
                        <p className="text-sm text-gray-500">For personal use and shopping</p>
                      </label>
                    </div>
                    {formData.userType === 'regular' && (
                      <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">Current</span>
                    )}
                  </div>

                  <div className="flex items-center justify-between p-3 border rounded-md">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="business-account"
                        name="userType"
                        checked={formData.userType === 'business'}
                        onChange={(e) => {
                          e.preventDefault(); // Prevent form submission
                          if (formData.userType !== 'business') {
                            if (window.confirm('Are you sure you want to upgrade to a Business account?')) {
                              handleAccountTypeChange('business');
                            }
                          }
                        }}
                        className="mr-2"
                      />
                      <label htmlFor="business-account">
                        <span className="font-medium">Business Customer</span>
                        <p className="text-sm text-gray-500">For business purchases and bulk orders</p>
                      </label>
                    </div>
                    {formData.userType === 'business' && (
                      <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">Current</span>
                    )}
                  </div>
                </div>

                {/* Business upgrade payment form - keep this part unchanged */}
                {showBusinessUpgradeForm && (
                  <div className="mt-4 p-4 border rounded-md">
                    <h3 className="text-lg font-medium mb-3">Business Account Verification</h3>
                    <Elements stripe={stripePromise}>
                      <BusinessUserPayment
                        amount={config.businessUserChargingAmount || 1.00}
                        email={formData.email}
                        onPaymentComplete={handleBusinessVerificationComplete}
                        saveCardOption={true}
                        error={businessPaymentError}
                        processing={businessPaymentProcessing}
                      />
                    </Elements>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'address':
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold">Manage Addresses</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {(formData.addresses || []).map((address, index) => (
                <div key={index} className="bg-white shadow-md rounded-lg p-6 border border-gray-200">
                  <h4 className="text-lg font-semibold text-gray-700">
                    Address {index + 1}</h4>
                  <div className="mt-4 space-y-2">
                    <AddressAutocomplete
                      index={index}
                      address={address}
                      onAddressChange={handleAddressChange}
                    />

                    <label className="block text-sm font-medium text-gray-600">City</label>
                    <input
                      type="text"
                      value={address.city}
                      onChange={(e) => handleAddressChange(index, 'city', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    />

                    <label className="block text-sm font-medium text-gray-600">State/Province</label>
                    <input
                      type="text"
                      value={address.state}
                      onChange={(e) => handleAddressChange(index, 'state', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    />

                    <label className="block text-sm font-medium text-gray-600">Postal Code</label>
                    <input
                      type="text"
                      value={address.postalCode}
                      onChange={(e) => handleAddressChange(index, 'postalCode', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    />

                    <label className="block text-sm font-medium text-gray-600">Country</label>
                    <select
                      value={address.country}
                      onChange={(e) => handleAddressChange(index, 'country', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    >
                      <option value="Canada">Canada</option>
                      <option value="United States">United States</option>
                    </select>
                  </div>

                  <div className="flex justify-between mt-4">
                    <button
                      type="button"
                      onClick={() => handleRemoveAddress(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSetDefaultAddress(index)}
                      className={`text-blue-600 hover:underline ${formData.defaultAddress === address._id ? 'font-bold' : ''}`}
                    >
                      {formData.defaultAddress === address._id ? 'Default' : 'Set as Default'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={handleAddAddress}
              className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Add New Address
            </button>
          </div>
        );

      case 'orders':
        // Ensure orders is an array
        const orderArray = Array.isArray(orders) ? orders :
          (orders && typeof orders === 'object' && Object.values(orders).length > 0) ?
            Object.values(orders) : [];

        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold mb-4">Order History</h2>
            {orderArray.length > 0 ? (
              <div className="grid gap-4">
                {orderArray.map((order) => {
                  // Ensure order is a valid object
                  if (!order || typeof order !== 'object') return null;

                  const orderId = order._id || 'unknown';
                  const createdAt = order.createdAt ? new Date(order.createdAt) : new Date();
                  const totalAmount = order.totalAmount || 0;
                  const status = order.status || 'pending';

                  return (
                    <div
                      key={orderId}
                      className="bg-white rounded-lg shadow p-4 border border-gray-200"
                    >
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="font-medium text-gray-800 truncate">
                          Order #{typeof orderId === 'string' ? orderId.substring(Math.max(0, orderId.length - 8)) : orderId}
                        </h3>
                        <span className={`px-2 py-1 text-xs leading-5 font-semibold rounded-full 
                            ${status === 'completed' ? 'bg-green-100 text-green-800' :
                            status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                              status === 'cancelled' ? 'bg-red-100 text-red-800' :
                                'bg-blue-100 text-blue-800'}`}>
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <p className="text-gray-500">Date</p>
                          <p>{createdAt.toLocaleDateString()}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Total</p>
                          <p className="font-medium">${parseFloat(totalAmount).toFixed(2)}</p>
                        </div>
                      </div>

                      <div className="mt-4 pt-2 border-t border-gray-100">
                        <a
                          href={`/orders`}
                          className="block w-full text-center py-2 text-blue-600 font-medium rounded hover:bg-blue-50"
                        >
                          View Details
                        </a>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-6 text-center">
                <div className="flex justify-center mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 160" className="w-40">
                    {/* Background with subtle gradient */}
                    <defs>
                      <linearGradient id="emptyOrderGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#fcfcfd" stopOpacity="1" />
                        <stop offset="100%" stopColor="#f9fafb" stopOpacity="1" />
                      </linearGradient>
                      <filter id="orderShadow" x="-10%" y="-10%" width="120%" height="120%">
                        <feDropShadow dx="0" dy="1" stdDeviation="1" floodOpacity="0.05" />
                      </filter>
                    </defs>

                    {/* Order paper */}
                    <rect x="70" y="20" width="100" height="120" rx="4" fill="white" stroke="#e5e7eb" strokeWidth="1.5" filter="url(#orderShadow)" />

                    {/* Order lines */}
                    <line x1="85" y1="40" x2="155" y2="40" stroke="#e5e7eb" strokeWidth="1.5" />
                    <line x1="85" y1="60" x2="155" y2="60" stroke="#e5e7eb" strokeWidth="1.5" />
                    <line x1="85" y1="80" x2="155" y2="80" stroke="#e5e7eb" strokeWidth="1.5" />
                    <line x1="85" y1="100" x2="155" y2="100" stroke="#e5e7eb" strokeWidth="1.5" />

                    {/* Search icon */}
                    <circle cx="150" cy="70" r="30" fill="none" stroke="#9ca3af" strokeWidth="1.5" opacity="0.7" />
                    <line x1="170" y1="90" x2="185" y2="105" stroke="#9ca3af" strokeWidth="2" opacity="0.7" />

                    {/* X mark for "not found" */}
                    <line x1="120" y1="50" x2="140" y2="70" stroke="#d1d5db" strokeWidth="2" />
                    <line x1="140" y1="50" x2="120" y2="70" stroke="#d1d5db" strokeWidth="2" />
                  </svg>
                </div>
                <p className="text-gray-500">No orders found.</p>
              </div>
            )}
          </div>
        );

      case 'coupons':
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold mb-4">My Coupons</h2>
            {coupons.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {coupons.map((coupon) => (
                  <div key={coupon._id} className="border rounded-lg p-4 bg-white shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start">
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <Tag className="h-5 w-5 text-blue-600" />
                          <h3 className="text-lg font-medium text-blue-600">{coupon.code}</h3>
                        </div>
                        <p className="text-2xl font-bold">
                          {coupon.discountType === 'percentage'
                            ? `${coupon.discountValue}% OFF`
                            : `$${coupon.discountValue} OFF`}
                        </p>
                        <div className="space-y-1">
                          <p className="text-sm text-gray-600">
                            Minimum purchase: ${coupon.minimumPurchase || 0}
                          </p>
                          {coupon.maxUsesPerUser > 0 && (
                            <p className="text-sm text-gray-600">
                              Max Uses: {coupon.maxUsesPerUser - (coupon.userUsage?.usageCount || 0)}
                            </p>
                          )}
                        </div>
                      </div>
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${new Date(coupon.endDate) > new Date()
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                        }`}>
                        {new Date(coupon.endDate) > new Date() ? 'Active' : 'Expired'}
                      </span>
                    </div>
                    <div className="mt-4 pt-4 border-t">
                      <div className="flex justify-between items-center">
                        <div className="text-sm text-gray-500">
                          Valid until: {new Date(coupon.endDate).toLocaleDateString()}
                        </div>
                        {/* <button 
                            onClick={() => navigator.clipboard.writeText(coupon.code)}
                            className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                          >
                            Copy Code
                          </button> */}
                        <CopyButton code={coupon.code} />

                      </div>
                      {coupon.conditions && (
                        <p className="mt-2 text-sm text-gray-500">
                          Conditions: {coupon.conditions}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <Tag className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No Coupons Available</h3>
                <p className="mt-2 text-gray-500">Check back later for new offers and discounts.</p>
              </div>
            )}
          </div>
        );

      case 'road-test':
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Road Test Requests</h2>
              <button
                onClick={() => navigate('/road-test-booking')}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
              >
                <Calendar className="h-4 w-4 mr-2" />
                Request Road Test
              </button>
            </div>

            {loadingRoadTests ? (
              <div className="flex justify-center py-8"><Loader2 className="animate-spin" size={24} /></div>
            ) : roadTestRequests.length > 0 ? (
              <div className="grid gap-4">
                {roadTestRequests.map((req) => (
                  <div key={req._id} className="bg-white rounded-lg shadow p-4 border border-gray-200">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-medium text-gray-800">{req.requestNumber || req._id}</h3>
                        <p className="text-sm text-gray-500">Created {new Date(req.createdAt).toLocaleDateString()}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs leading-5 font-semibold rounded-full ${req.status === 'booked' ? 'bg-green-100 text-green-800' :
                        req.status === 'searching' ? 'bg-blue-100 text-blue-800' :
                          req.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                        }`}>
                        {req.status?.replace('_', ' ') || 'pending'}
                      </span>
                    </div>

                    {/* Appointment details if booked */}
                    {req.status === 'booked' && req.appointment && (
                      <div className="mt-3 p-3 rounded bg-green-50 border border-green-200">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                          <div className="flex items-center space-x-2">
                            <Calendar className="w-4 h-4 text-green-600" />
                            <span>{req.appointment.date ? new Date(req.appointment.date).toLocaleDateString() : '—'}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Clock className="w-4 h-4 text-green-600" />
                            <span>{req.appointment.time || '—'}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <MapPin className="w-4 h-4 text-green-600" />
                            <span>{req.appointment.office || '—'}</span>
                          </div>
                        </div>
                        {req.appointment.confirmationNumber && (
                          <p className="mt-2 text-xs text-green-800">Confirmation: {req.appointment.confirmationNumber}</p>
                        )}
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm mt-3">
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <span>{(req.startDate && req.endDate) ? `${new Date(req.startDate).toLocaleDateString()} – ${new Date(req.endDate).toLocaleDateString()}` : 'N/A'}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <MapPin className="w-4 h-4 text-gray-500" />
                        <span>{(req.preferredOffices && req.preferredOffices.length > 0) ? req.preferredOffices.join(', ') : 'Any office'}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Clock className="w-4 h-4 text-gray-500" />
                        <span>{(req.preferredTimes && req.preferredTimes.length > 0) ? req.preferredTimes.join(', ') : (req.searchSettings?.anyTime ? 'Any time' : 'Not specified')}</span>
                      </div>
                    </div>
                    <div className="mt-3 flex justify-end gap-2"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="text-lg font-medium text-blue-900 mb-2">Book Your Road Test</h3>
                <div className="space-y-2 text-sm text-blue-800">
                  <p>• Submit your request with preferred date range and locations</p>
                  <p>• Our system will automatically search for available appointments</p>
                  <p>• When an appointment is found, you'll be notified immediately</p>
                  <p>• The appointment will be automatically booked on your behalf</p>
                </div>
                <div className="mt-4">
                  <button onClick={() => navigate('/road-test-booking')} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">Start Booking Process</button>
                </div>
              </div>
            )}
          </div>
        );

      case 'support':
        return <div><SupportSection /></div>;

      case 'payments':
        return <PaymentMethodsManagement />;

      case 'security':
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold mb-4">Security Settings</h2>
            <div className="space-y-6">
              {/* Password Change Section */}
              <div className="bg-white p-6 rounded-lg">
                <h3 className="text-lg font-medium mb-4">Change Password</h3>
                <div className="space-y-4">
                  <div className="relative">
                    <label className="block text-sm font-medium mb-1">Current Password</label>
                    <div className="relative">
                      <input
                        type={showCurrentPassword ? "text" : "password"}
                        name="currentPassword"
                        value={formData.currentPassword}
                        onChange={handleInputChange}
                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-2 top-1/2 -translate-y-1/2"
                        style={{
                          boxShadow: 'none',
                          border: 'none',
                          background: 'transparent',
                          transform: 'none',
                          padding: 0,
                          overflow: 'visible',
                          marginTop: -10 + 'px'
                        }}
                      >
                        {showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-medium mb-1">New Password</label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? "text" : "password"}
                        name="newPassword"
                        value={formData.newPassword}
                        onChange={handleInputChange}
                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-2 top-1/2 -translate-y-1/2"
                        style={{
                          boxShadow: 'none',
                          border: 'none',
                          background: 'transparent',
                          transform: 'none',
                          padding: 0,
                          overflow: 'visible',
                          marginTop: -10 + 'px'
                        }}
                      >
                        {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Login Activity Section */}
              <div className="bg-white p-6 rounded-lg">
                <h3 className="text-lg font-medium mb-4">Login Activity</h3>
                <div className="space-y-4">
                  {loadingActivity ? (
                    <div className="flex justify-center">
                      <Loader2 className="animate-spin" size={24} />
                    </div>
                  ) : (
                    <>
                      {loginActivity.slice(0, showAllActivity ? undefined : 1).map((activity, index) => (
                        <div key={index} className="flex justify-between items-center border-b pb-2">
                          <div>
                            <p className="font-medium">Login from {activity.location || 'Unknown Location'}</p>
                            <p className="text-sm text-gray-500">
                              {activity.device ? activity.device.split('(')[0] : 'Unknown Device'}
                            </p>
                          </div>
                          <p className="text-sm text-gray-500">
                            {new Date(activity.timestamp).toLocaleString()}
                          </p>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setShowAllActivity(!showAllActivity);
                        }}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        {showAllActivity ? 'Show Less' : 'View all activity'}
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Account Deletion Section */}
              {/* <div className="bg-white p-6 rounded-lg">
                  <h3 className="text-lg font-medium mb-4">Account Deletion</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Once you delete your account, there is no going back. Please be certain.
                  </p>
                  <button
                    type="button"
                    onClick={() => handleDeleteAccount()}
                    className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                  >
                    Delete Account
                  </button>
                </div> */}
            </div>
          </div>
        );

      case 'notifications':
        const handlePreferenceChange = async (e) => {
          e.preventDefault();
          const { name, checked } = e.target;
          const [section, field] = name.split('.');

          // Create a new preferences object with all existing values
          const updatedPreferences = {
            ...formData.preferences,
            [field]: checked
          };

          // Update local state immediately for better UX
          setFormData(prev => ({
            ...prev,
            preferences: updatedPreferences
          }));

          try {
            const response = await axios.put('/api/users/preferences', {
              preferences: updatedPreferences
            }, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              },
            });

            const data = await response.data;

            if (response.status === 200) {
              // Update with the complete preferences object from the server
              setFormData(prev => ({
                ...prev,
                preferences: data.preferences
              }));
              setMessage({ type: 'success', content: 'Preferences updated successfully!' });
            } else {
              // Revert local state if update failed
              setFormData(prev => ({
                ...prev,
                preferences: {
                  ...prev.preferences,
                  [field]: !checked
                }
              }));
              setMessage({ type: 'error', content: data.error || 'Failed to update preferences' });
            }
          } catch (error) {
            // Revert local state if update failed
            setFormData(prev => ({
              ...prev,
              preferences: {
                ...prev.preferences,
                [field]: !checked
              }
            }));
            console.error('Error updating preferences:', error);
            setMessage({ type: 'error', content: 'Error updating preferences' });
          }
        };

        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold mb-4">Notification Preferences</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-white rounded-lg">
                <div>
                  <h3 className="font-medium">Order Updates</h3>
                  <p className="text-sm text-gray-500">Receive notifications about your order status</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="preferences.orderUpdates"
                    checked={formData.preferences.orderUpdates}
                    onChange={handlePreferenceChange}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-white rounded-lg">
                <div>
                  <h3 className="font-medium">Promotional Emails</h3>
                  <p className="text-sm text-gray-500">Receive emails about sales and special offers</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="preferences.marketingEmails"
                    checked={formData.preferences.marketingEmails}
                    onChange={handlePreferenceChange}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-white rounded-lg">
                <div>
                  <h3 className="font-medium">Newsletter</h3>
                  <p className="text-sm text-gray-500">Receive our monthly newsletter</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="preferences.newsletter"
                    checked={formData.preferences.newsletter}
                    onChange={handlePreferenceChange}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-white rounded-lg">
                <div>
                  <h3 className="font-medium">Marketing Checkout</h3>
                  <p className="text-sm text-gray-500">Receive marketing communications during checkout</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="preferences.promotionalAlerts"
                    checked={formData.preferences.promotionalAlerts}
                    onChange={handlePreferenceChange}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          </div>
        );

      case 'preferences':
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold mb-4">Account Preferences</h2>
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg">
                <h3 className="text-lg font-medium mb-4">Language & Region</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Language</label>
                    <select
                      className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                      defaultValue="en"
                    >
                      <option value="en">English</option>
                      <option value="fr">Français</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Time Zone</label>
                    <select
                      className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                      defaultValue="EST"
                    >
                      <option value="EST">Eastern Time (ET)</option>
                      <option value="CST">Central Time (CT)</option>
                      <option value="MST">Mountain Time (MT)</option>
                      <option value="PST">Pacific Time (PT)</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg">
                <h3 className="text-lg font-medium mb-4">Privacy Settings</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Profile Visibility</p>
                      <p className="text-sm text-gray-500">Control who can see your profile</p>
                    </div>
                    <select
                      className="p-2 border rounded focus:ring-2 focus:ring-blue-500"
                      defaultValue="private"
                    >
                      <option value="public">Public</option>
                      <option value="private">Private</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'reservations':
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold mb-4">My Reservations</h2>
            {loadingBookings ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : bookings.length > 0 ? (
              <div className="grid gap-4">
                {bookings.map((booking) => (
                  <div
                    key={booking._id}
                    className="bg-white rounded-lg shadow p-6 border border-gray-200"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {booking.service?.name || 'Service'}
                        </h3>
                        <p className="text-sm text-gray-500">
                          Booking #{booking.bookingNumber}
                        </p>
                      </div>
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${booking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                        booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          booking.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                            booking.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                        }`}>
                        {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-500">Date</p>
                          <p className="text-gray-900">
                            {new Date(booking.bookingDate).toLocaleDateString()}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Clock className="w-4 h-4 text-gray-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-500">Time</p>
                          <p className="text-gray-900">
                            {booking.startTime} - {booking.endTime}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Clock className="w-4 h-4 text-gray-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-500">Duration</p>
                          <p className="text-gray-900">{booking.duration} minutes</p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <CreditCard className="w-4 h-4 text-gray-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-500">Payment</p>
                          <p className="text-gray-900">
                            ${booking.pricing?.totalAmount?.toFixed(2) || '0.00'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {booking.service?.category && (
                      <div className="mb-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {booking.service.category.replace('_', ' ')}
                        </span>
                      </div>
                    )}

                    <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                      <a
                        href={`/booking-confirmation/${booking.bookingNumber}?email=${user.email}`}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        View Details
                      </a>

                      {booking.status === 'confirmed' && (
                        <button
                          onClick={() => {
                            const link = `${window.location.origin}/booking-confirmation/${booking.bookingNumber}?email=${user.email}`;
                            navigator.clipboard.writeText(link);
                            setMessage({ type: 'success', content: 'Booking link copied to clipboard!' });
                            setTimeout(() => setMessage({ type: '', content: '' }), 3000);
                          }}
                          className="text-gray-600 hover:text-gray-800 font-medium"
                        >
                          Copy Link
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No Reservations Found</h3>
                <p className="mt-2 text-gray-500">You haven't made any reservations yet.</p>
                <a
                  href="/"
                  className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Book a Service
                </a>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="py-16">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar */}
          <div className="w-full md:w-64 space-y-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    if (window.innerWidth <= 768) {
                      window.scrollBy({ top: 300, behavior: 'smooth' });
                    }
                  }}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${activeTab === tab.id
                    ? 'bg-blue-200 font-normal text-blue-900'
                    : 'text-gray-600 font-normal hover:bg-gray-200'
                    }`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}

            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <LogOut className="h-5 w-5" />
              <span>Log Out</span>
            </button>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              {message.content && (
                <Alert variant={message.type === 'error' ? 'destructive' : 'default'} className="mb-6">
                  <AlertDescription>{message.content}</AlertDescription>
                </Alert>
              )}

              {/* Only wrap form around editable sections */}
              {['personal', 'address', 'notifications', 'security', 'preferences'].includes(activeTab) ? (
                <form onSubmit={handleSubmit}>
                  {renderTabContent()}
                  <div className="mt-6 flex justify-end">
                    <button
                      type="submit"
                      disabled={loading}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="animate-spin mr-2" size={20} />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="mr-2" size={20} />
                          Save Changes
                        </>
                      )}
                    </button>
                  </div>
                </form>
              ) : (
                renderTabContent()
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;